import { AnimatePresence, motion } from "framer-motion";
import { useSetAtom } from "jotai";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import ReactFocusLock from "react-focus-lock";

import { ChevronIcon } from "@theme/assets";
import { Box, Divider, Flex, MsButton, NavProductList, Text } from "@theme/components";
import { NavShopAllLink, drawerOpenAtom } from "@theme/components/Navigation";
import { NavCategory } from "@theme/types";

interface MobileNavCategoryScreenProps {
  navCategory: NavCategory | null;
  closeAccordion: () => void;
}

export const MobileNavCategoryScreen: FC<MobileNavCategoryScreenProps> = ({
  navCategory,
  closeAccordion,
}) => {
  const setIsDrawerOpen = useSetAtom(drawerOpenAtom);

  const columns = navCategory?.navColumns;

  const firstSubCategoryId = columns?.[0]?.items[0].id;

  const [activeIndexes, setActiveIndexes] = useState<Array<string>>([]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleAccordionToggle = (id: string) => {
    const isActive = activeIndexes.includes(id);
    if (isActive) {
      setActiveIndexes(activeIndexes.filter((activeIndex) => activeIndex !== id));
      return;
    }

    setActiveIndexes([...activeIndexes, id]);
  };

  const backButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // There is a bug on mobile devices, where focusing will cause the rest of the elements
    // not to render properly with AnimatePresence when we try to focus.
    // Using onAnimationComplete to focus on the back button after elements properly render.
    if (backButtonRef.current && isMenuOpen) {
      backButtonRef.current.focus();
    }
    // Always open the first sub category when screen opens
    if (firstSubCategoryId) {
      setActiveIndexes([firstSubCategoryId]);
    }
  }, [firstSubCategoryId, isMenuOpen]);

  return (
    <AnimatePresence>
      {navCategory && (
        <motion.div
          variants={{
            hide: { x: "100%" },
            show: { x: 0 },
          }}
          initial="hide"
          animate="show"
          exit="hide"
          transition={{ ease: [1, 0, 0, 1], duration: 0.3 }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "full",
            bg: "beige50",
          }}
          onAnimationComplete={(variant) => setIsMenuOpen(variant === "show")}
        >
          <ReactFocusLock returnFocus={true} disabled={!isMenuOpen}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "s200",
                alignSelf: "stretch",
                pt: ["s400", "s600"],
                px: ["s600", "s700"],
                pb: "s1400",
              }}
            >
              <Flex
                sx={{
                  flex: "1 0 0",
                  pb: 0,
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "s400",
                  alignSelf: "stretch",
                  cursor: "pointer",
                }}
                onClick={closeAccordion}
              >
                <MsButton
                  variant="link"
                  onClick={closeAccordion}
                  ref={backButtonRef}
                  aria-label="Back to main navigation menu"
                  tabIndex={0}
                  sx={{
                    display: "flex",
                    px: 0,
                    alignItems: "center",
                    alignSelf: "stretch",
                    fontSize: "inherit",
                  }}
                >
                  <ChevronIcon
                    sx={{
                      transform: "rotate(180deg)",
                      height: "14px",
                      color: "grey500",
                      mr: "s300",
                    }}
                  />
                  <Text variant="sansBody02Regular" sx={{ color: "grey700" }}>
                    {navCategory.backCTAText}
                  </Text>
                </MsButton>
              </Flex>
              <Text
                variant="sansBody03Medium"
                sx={{
                  color: "black",
                }}
              >
                {navCategory.title}
              </Text>
              <Divider aria-hidden={true} sx={{ my: "s400", color: "grey100" }} />
              <NavShopAllLink
                to={`${navCategory.pageSlug}`}
                onClick={() => setIsDrawerOpen(false)}
                text={navCategory.shopAllCTAText}
                ariaLabel={navCategory.shopAllCTAText}
              />
              <Box data-testid={`mobile-${navCategory.slug}-subcategories`} sx={{ mt: "s500" }}>
                {columns?.map(
                  (column, columnIndex) =>
                    column.items.length > 0 && (
                      <Box key={columnIndex}>
                        {column.items.map((subCategory) => {
                          const isActive = activeIndexes.includes(subCategory.id);

                          return (
                            <Fragment key={subCategory.id}>
                              <MsButton
                                data-testid={`mobile-nav-${subCategory.slug}`}
                                variant="link"
                                sx={{
                                  mb: "s500",
                                  p: 0,
                                  width: "full",
                                  span: {
                                    justifyContent: "space-between",
                                    span: {
                                      fontSize: "fs18",
                                      color: "grey700",
                                      lineHeight: "lh400",
                                      textTransform: "capitalize",
                                    },
                                    svg: {
                                      transform: isActive ? "rotate(270deg)" : "rotate(90deg)",
                                      height: "14px",
                                    },
                                  },
                                }}
                                onClick={() => handleAccordionToggle(subCategory.id)}
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleAccordionToggle(subCategory.id);
                                  }
                                }}
                                aria-label={`${subCategory?.title} accordion content`}
                                aria-expanded={isActive}
                                aria-controls={`accordion-content-${subCategory.id}`}
                                TrailingIcon={ChevronIcon}
                                iconColor="Gold"
                              >
                                {subCategory?.title}
                              </MsButton>

                              <Box
                                id={`accordion-content-${subCategory.id}`}
                                aria-hidden={!isActive}
                                sx={{
                                  display: isActive ? "block" : "none",
                                  pl: ["s700", null, null, 0],
                                }}
                              >
                                <NavProductList
                                  navCategory={navCategory}
                                  subCategory={subCategory}
                                  closeNavigation={() => setIsDrawerOpen(false)}
                                />
                              </Box>
                            </Fragment>
                          );
                        })}
                      </Box>
                    ),
                )}
              </Box>
            </Box>
          </ReactFocusLock>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
